import { Info, Minus, TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import { trendRichtung } from "../../lib/kennzahl-trend.js";
import { KEIN_WERT } from "../../lib/probezeit-anzeige.js";

interface KpiCardProps {
  label: string;
  // Ohne Wert (null), wenn hinter der Kennzahl niemand in der Stichprobe steht: Statt der Zahl steht der
  // Strich der Probezeit-Anzeige (KEIN_WERT), Einheit und Trend entfallen. Eine echte 0 ist ein Wert.
  value: number | null;
  unit: string;
  trend: number;
  inverse?: boolean; // true = lower is better (risks), false = higher is better (indexes)
  info?: string; // tooltip explaining the KPI
  basis?: string; // Datenbasis hinter der Kennzahl, z.B. "1 von 1"
  // Vorbehalt zur Datenbasis, klein unter `basis`, z.B. "beruht auf 5 Personen". Fehlt er oder ist
  // er null, zeichnet die Kachel nichts dazu.
  hinweis?: string | null;
}

export function KpiCard({
  label,
  value,
  unit,
  trend,
  inverse = false,
  info,
  basis,
  hinweis,
}: KpiCardProps) {
  const good = inverse ? trend < 0 : trend > 0;
  const bad = inverse ? trend > 0 : trend < 0;
  const [showInfo, setShowInfo] = useState(false);

  return (
    <div className="card card-hover p-5 flex flex-col gap-3 relative">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>
          {label}
        </p>
        {info && (
          <button
            type="button"
            className="p-0.5 rounded-full transition-colors"
            style={{ color: "var(--text-muted)" }}
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo(!showInfo)}
          >
            <Info size={13} />
          </button>
        )}
      </div>
      {showInfo && info && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg p-3 text-xs leading-relaxed"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            color: "var(--text-secondary)",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          }}
        >
          {info}
        </div>
      )}
      {/* Ohne Wert steht der Strich da, blass wie in der Liste „In der Probezeit“. Einheit und Trend
          entfallen: Ein Trend gegen nichts sagt nichts. Mit einer Zahl bleibt das Markup, wie es war. */}
      <div className="flex items-end justify-between">
        <span
          className="font-mono-data text-3xl font-medium"
          style={{ color: value === null ? "var(--text-muted)" : "var(--text-primary)" }}
        >
          {value ?? KEIN_WERT}
          {value !== null && (
            <span className="text-base ml-0.5" style={{ color: "var(--text-muted)" }}>
              {unit}
            </span>
          )}
        </span>
        {value !== null && (
          <div
            className="flex items-center gap-1 text-xs font-medium"
            style={{
              color: good ? "var(--risk-low)" : bad ? "var(--risk-high)" : "var(--text-muted)",
            }}
          >
            {/* Der Pfeil folgt der Zahl, die Bewertung steckt in der Farbe darüber.
                Regel und Begründung (F29): lib/kennzahl-trend.ts */}
            {trendRichtung(trend) === "gleich" ? (
              <Minus size={13} />
            ) : trendRichtung(trend) === "hoch" ? (
              <TrendingUp size={13} />
            ) : (
              <TrendingDown size={13} />
            )}
            {trend > 0 ? `+${trend}` : trend}
            {unit}
          </div>
        )}
      </div>
      {basis && (
        <p className="text-[11px] -mt-1" style={{ color: "var(--text-muted)" }}>
          {basis}
        </p>
      )}
      {hinweis && (
        <p className="text-[11px]" style={{ color: "var(--text-muted)" }}>
          {hinweis}
        </p>
      )}
    </div>
  );
}
